import type {
  OrgPricingTier,
  SubscriptionType,
} from '@/features/billing/types';
import {
  Badge,
  Box,
  Heading,
  HStack,
  Icon,
  List,
  ListItem,
  Text,
  useColorModeValue,
  VStack,
} from '@/ui';
import { OrgPricingCta } from './OrgPricingCta';

type Props = {
  tier: OrgPricingTier;
  subscriptionType?: SubscriptionType;
  /** Drop the card border/padding, for a lone card rendered directly in the modal. */
  frameless?: boolean;
  /** Omit the action button, for layouts that pin the CTA outside the card. */
  hideCta?: boolean;
};

// Card for the compare-plans table.
export const OrgPricingCard = ({
  tier,
  subscriptionType = 'yearly',
  frameless = false,
  hideCta = false,
}: Props) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const frameStyles = frameless
    ? {}
    : {
        _hover: { shadow: 'md' },
        bg: cardBg,
        border: '1px',
        borderColor: tier.isPopular ? '#5D3FF6' : borderColor,
        borderRadius: '2xl',
        p: { base: 4, md: 6 },
        shadow: tier.isPopular ? 'md' : 'sm',
        transition: 'all 0.2s',
      };

  return (
    <Box
      {...frameStyles}
      display="flex"
      flexDirection="column"
      h="full"
      position="relative"
    >
      {tier.isPopular && (
        <Badge
          bg="#5D3FF6"
          borderRadius="full"
          color="white"
          fontSize="10px"
          fontWeight="700"
          left="50%"
          letterSpacing="wide"
          position="absolute"
          px={3}
          py={1}
          textTransform="uppercase"
          top={0}
          transform="translate(-50%, -50%)"
        >
          Most popular
        </Badge>
      )}
      <VStack
        alignItems="flex-start"
        flex="1"
        spacing={{ base: 3, md: 6 }}
        textAlign="left"
        w="full"
      >
        {/* Header */}
        <VStack align="flex-start" spacing={2}>
          <Heading
            fontWeight="bold"
            pb={{ base: 0, md: 2 }}
            size={{ base: 'md', md: 'lg' }}
          >
            {tier.name}
          </Heading>
          <HStack align="baseline" spacing={1}>
            <Heading fontWeight="bold" size={{ base: 'xl', md: '2xl' }}>
              {tier.price}
            </Heading>
            {tier.period && <Text>{tier.period}</Text>}
          </HStack>
          {tier.priceSubLabel && (
            <Text color="gray.600" fontSize="sm">
              {tier.priceSubLabel}
            </Text>
          )}
        </VStack>

        {/* Description */}
        <Text fontWeight="bold">{tier.description}</Text>

        {/* Features */}
        <List alignContent="flex-start" spacing={{ base: 2, md: 3 }}>
          {tier.features.map((feature, index) => (
            <ListItem key={index}>
              <HStack spacing={3}>
                <Box
                  alignItems="center"
                  bg="purple.200"
                  borderRadius="full"
                  display="flex"
                  justifyContent="center"
                  p={1}
                >
                  <Icon
                    color="#5D3FF6"
                    height="16px"
                    icon="Checkmark"
                    width="16px"
                  />
                </Box>
                <Text fontSize="sm" fontWeight="medium">
                  {feature.text}
                </Text>
              </HStack>
            </ListItem>
          ))}
        </List>

        {/* CTA — `mt="auto"` pins it to the foot of the card so the buttons line
            up across columns whose features and seat picker differ in height. */}
        {!hideCta && (
          <Box mt="auto" pt={{ base: 2, md: 4 }} w="full">
            <OrgPricingCta subscriptionType={subscriptionType} tier={tier} />
          </Box>
        )}
      </VStack>
    </Box>
  );
};
